import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import UserProfileService from '../services/UserProfileService';

const defaultProfile = {
  companyName: '',
  employeesCount: ''
};

const OnboardingDialog = ({
  open,
  defaults = defaultProfile,
  onSaved
}) => {
  const [companyName, setCompanyName] = useState(defaults.companyName || '');
  const [employeesCount, setEmployeesCount] = useState(
    defaults.employeesCount ? String(defaults.employeesCount) : ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (open) {
      setCompanyName(defaults.companyName || '');
      setEmployeesCount(
        defaults.employeesCount !== undefined && defaults.employeesCount !== null
          ? String(defaults.employeesCount)
          : ''
      );
      setErrorMessage('');
      setAcceptedTerms(false);
    }
  }, [open, defaults]);

  const handleCloseRequest = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setErrorMessage('');

    try {
      const trimmedName = companyName.trim();
      const parsedEmployees = parseInt(employeesCount, 10);

      if (!trimmedName) {
        throw new Error('Ingresa el nombre de la empresa.');
      }

      if (Number.isNaN(parsedEmployees) || parsedEmployees <= 0) {
        throw new Error('Ingresa un número de empleados válido.');
      }

      if (!acceptedTerms) {
        throw new Error('Debes aceptar los términos de uso para continuar.');
      }

      const createdProfile = await UserProfileService.createProfile({
        companyName: trimmedName,
        employeesCount: parsedEmployees,
        acceptedTerms
      });

      const normalizedProfile = {
        companyName:
          createdProfile?.company_name ||
          createdProfile?.companyName ||
          trimmedName,
        employeesCount:
          createdProfile?.employees_count ??
          createdProfile?.employeesCount ??
          parsedEmployees,
        userId: createdProfile?.user_id || createdProfile?.userId || null,
        // `account_id` (no `account_uuid`) es la clave real que devuelve el
        // backend -- create_user_account() genera su propio UUID
        // server-side e ignora el que el cliente manda (ver
        // whisperbi-back-security/functions/user-account/app.py). Leer el
        // campo equivocado acá hacía que el resto de la sesión quedara con
        // un accountId viejo/incorrecto hasta recargar la página.
        accountId: createdProfile?.account_id || null
      };

      if (typeof onSaved === 'function') {
        onSaved(normalizedProfile);
      }
    } catch (error) {
      console.error('OnboardingDialog.handleSubmit error:', error);
      setErrorMessage(
        error?.message || 'No pudimos guardar la información. Intenta nuevamente.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={handleCloseRequest}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: {
          backgroundColor: 'var(--wb-bg-deep)',
          border: '1px solid var(--wb-orange)',
          borderRadius: 4,
          color: 'var(--wb-text-strong)',
          width: '100%',
          maxWidth: 520
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--wb-text-strong)' }}>
            Configuración inicial
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--wb-orange)', mt: 1 }}>
            Configura el perfil de tu empresa para comenzar
          </Typography>
          <Box
            sx={{
              height: 4,
              width: 64,
              backgroundColor: 'var(--wb-orange)',
              borderRadius: 2,
              mt: 2,
              mx: 'auto'
            }}
          />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" sx={{ color: 'var(--wb-text)', mb: 2 }}>
          Generaremos un perfil de empresa vinculado a tu usuario. El siguiente
          paso será conectar tu primera fuente de datos en Conexiones para que
          puedas empezar a hacer preguntas.
        </Typography>

        <TextField
          fullWidth
          required
          label="Nombre de la empresa"
          value={companyName}
          onChange={(event) => {
            setCompanyName(event.target.value);
            if (errorMessage) {
              setErrorMessage('');
            }
          }}
          variant="outlined"
          autoComplete="organization"
          sx={{
            mt: 1.5,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'var(--wb-x-bg-111827, #111827)',
              color: 'var(--wb-text-strong)',
              '& fieldset': {
                borderColor: 'var(--wb-x-border-fb923c44, #fb923c44)'
              },
              '&:hover fieldset': {
                borderColor: 'var(--wb-orange)'
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--wb-blue-google)'
              }
            },
            '& .MuiInputLabel-root': {
              color: 'var(--wb-orange)'
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'var(--wb-blue-google)'
            }
          }}
          placeholder="Ej. Acme Corp"
        />

        <TextField
          fullWidth
          required
          label="Número de empleados"
          value={employeesCount}
          onChange={(event) => {
            setEmployeesCount(event.target.value);
            if (errorMessage) {
              setErrorMessage('');
            }
          }}
          variant="outlined"
          type="number"
          inputProps={{ min: 1 }}
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'var(--wb-x-bg-111827, #111827)',
              color: 'var(--wb-text-strong)',
              '& fieldset': {
                borderColor: 'var(--wb-x-border-fb923c44, #fb923c44)'
              },
              '&:hover fieldset': {
                borderColor: 'var(--wb-orange)'
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--wb-blue-google)'
              }
            },
            '& .MuiInputLabel-root': {
              color: 'var(--wb-orange)'
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'var(--wb-blue-google)'
            }
          }}
          placeholder="Ej. 50"
        />

        <FormControlLabel
          sx={{ mt: 2, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { mt: '9px' } }}
          control={
            <Checkbox
              checked={acceptedTerms}
              onChange={(event) => {
                setAcceptedTerms(event.target.checked);
                if (errorMessage) {
                  setErrorMessage('');
                }
              }}
              sx={{ color: 'var(--wb-orange)', '&.Mui-checked': { color: 'var(--wb-blue-google)' } }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'var(--wb-text)' }}>
              Acepto los términos de uso de Whisper BI y autorizo el tratamiento de
              mis datos para crear la cuenta.
            </Typography>
          }
        />

        {errorMessage && (
          <Typography variant="body2" sx={{ color: 'var(--wb-danger)', mt: 2 }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={submitting || !acceptedTerms}
          sx={{
            width: '100%',
            py: 1.2,
            borderRadius: 999,
            fontWeight: 600,
            fontSize: '0.95rem',
            textTransform: 'none',
            backgroundColor: 'var(--wb-blue-google)',
            '&:hover': {
              backgroundColor: 'var(--wb-x-bg-1666ce, #1666ce)'
            },
            '&:disabled': {
              backgroundColor: 'var(--wb-x-bg-1a73e866, #1a73e866)'
            }
          }}
        >
          {submitting ? 'Guardando...' : 'Crear perfil'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingDialog;
